import { useState } from "react";
import axios from "axios";
import { TrendingUp, Users, Eye, Globe } from "lucide-react";
import { useSeo } from "@/components/Layout";
import { SectionLabel } from "@/components/ui-blocks";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = "gc-stats-pwd";

const Stat = ({ label, value, Icon }) => (
  <div className="border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-accentbrand mb-2"><Icon size={16} /><span className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span></div>
    <p className="font-display text-3xl font-black text-ink">{value}</p>
  </div>
);

const BarRow = ({ label, count, max }) => (
  <div className="grid grid-cols-[1fr_60px] gap-3 items-center py-2 border-t border-border">
    <div>
      <p className="text-sm text-ink truncate">{label}</p>
      <div className="mt-1 h-1.5 bg-secondary overflow-hidden">
        <div className="h-full bg-accentbrand" style={{ width: `${max ? (count / max) * 100 : 0}%` }} />
      </div>
    </div>
    <span className="text-sm font-bold text-ink text-right">{count}</span>
  </div>
);

export default function Stats() {
  useSeo("Analytics | Gihed Coaching", "Tableau de bord privé des statistiques du site.");
  const [pwd, setPwd] = useState(() => {
    try { return localStorage.getItem(KEY) || ""; } catch { return ""; }
  });
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (password, d) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/analytics/stats`, { params: { password, days: d } });
      setData(res.data);
      try { localStorage.setItem(KEY, password); } catch { /* ignore */ }
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur de chargement.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); load(pwd, days); };

  const maxPath = data?.top_paths?.[0]?.count || 0;
  const maxRef = data?.top_referrers?.[0]?.count || 0;

  return (
    <div className="min-h-screen">
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-12">
        <SectionLabel>Admin</SectionLabel>
        <h1 className="mt-4 display-2 text-ink">Statistiques du site.</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Suivi cookieless, RGPD-friendly. Aucun cookie, aucun identifiant personnel stocké. Les visiteurs sont anonymisés par un hash quotidien.</p>

        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <input
            type="password"
            data-testid="stats-password"
            placeholder="Mot de passe admin"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="flex-1 bg-card border border-border px-4 py-3 text-ink placeholder-muted-foreground focus:outline-none focus:border-ink"
          />
          <select
            data-testid="stats-days"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-card border border-border px-4 py-3 text-ink focus:outline-none focus:border-ink"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
          <button
            type="submit"
            data-testid="stats-load"
            disabled={loading || !pwd}
            className="bg-accentbrand text-white text-sm font-bold uppercase tracking-wide px-6 py-3 disabled:opacity-50"
          >
            {loading ? "Chargement…" : "Voir"}
          </button>
        </form>

        {error && <p className="mt-4 text-sm text-red-600" data-testid="stats-error">{error}</p>}

        {data && (
          <div className="mt-8 space-y-6" data-testid="stats-panel">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat label="Vues totales" value={data.total_views.toLocaleString("fr-FR")} Icon={Eye} />
              <Stat label="Visiteurs uniques" value={data.unique_visitors.toLocaleString("fr-FR")} Icon={Users} />
              <Stat label="Période" value={`${data.range_days}j`} Icon={TrendingUp} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="border border-border bg-card p-5">
                <h3 className="font-display text-lg font-bold text-ink mb-3">Pages les plus vues</h3>
                {data.top_paths.length === 0
                  ? <p className="text-sm text-muted-foreground">Aucune donnée pour cette période.</p>
                  : data.top_paths.map((p, i) => <BarRow key={i} label={p.path} count={p.count} max={maxPath} />)}
              </div>
              <div className="border border-border bg-card p-5">
                <h3 className="font-display text-lg font-bold text-ink mb-3 flex items-center gap-2"><Globe size={16} className="text-accentbrand" /> Sources de trafic</h3>
                {data.top_referrers.length === 0
                  ? <p className="text-sm text-muted-foreground">Trafic direct uniquement.</p>
                  : data.top_referrers.map((r, i) => <BarRow key={i} label={r.referrer} count={r.count} max={maxRef} />)}
              </div>
            </div>

            <div className="border border-border bg-card p-5">
              <h3 className="font-display text-lg font-bold text-ink mb-3">Vues par jour</h3>
              {data.daily.length === 0
                ? <p className="text-sm text-muted-foreground">Aucune donnée pour cette période.</p>
                : (
                  <div className="flex items-end gap-1 h-40">
                    {data.daily.map((d, i) => {
                      const max = Math.max(...data.daily.map((x) => x.views), 1);
                      const h = (d.views / max) * 100;
                      return (
                        <div key={i} title={`${d.date} · ${d.views} vues · ${d.visitors} visiteurs`} className="flex-1 group relative">
                          <div className="w-full bg-accentbrand hover:bg-[#E63E00] transition-colors" style={{ height: `${h}%`, minHeight: d.views > 0 ? "2px" : "0" }} />
                        </div>
                      );
                    })}
                  </div>
                )}
              <div className="flex justify-between mt-2 text-[10px] font-mono-alt text-muted-foreground">
                <span>{data.daily[0]?.date}</span>
                <span>{data.daily[data.daily.length - 1]?.date}</span>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
